import React, { type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
import omit from 'lodash/omit';
import type { ButtonProps } from '@atlaskit/button';
import Button from '@atlaskit/button/standard-button';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import EmojiObjectsIcon from '@atlaskit/icon/glyph/emoji/objects';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { codeFontFamily } from '@atlassian/jira-common-styles';
import { type IntlShapeV2 as IntlShape, injectIntlV2 as injectIntl } from '@atlassian/jira-intl';

import { PurpleButton } from '../purple-button';
import messages from './messages';

export type SpotlightButtonProps = {
	text: string;
} & ButtonProps;

export type Props = {
	heading?: string;
	children: ReactNode;
	steps?: {
		current: number;
		count: number;
	};
	buttons: {
		primary?: SpotlightButtonProps;
		secondary?: SpotlightButtonProps;
		close?: ButtonProps;
	};
	infoBox?: string;
};

type PropsWithIntl = Props & {
	intl: IntlShape;
};

export const ItsmSpotlightCard = injectIntl(
	({ heading, steps, buttons, children, intl, infoBox }: PropsWithIntl) => {
		const hasFooter = buttons.primary || buttons.secondary || steps;
		return (
			<SpotlightContent>
				<Body>
					<TextContent>
						{heading !== undefined && (
							<StyledTitleWrapper>
								<Heading size="small">{heading}</Heading>
							</StyledTitleWrapper>
						)}
						{children}
					</TextContent>
					{buttons.close && (
						<Button spacing="none" appearance="link" {...buttons.close}>
							<CrossIcon
								label={intl.formatMessage(messages.exitTourButtonLabel)}
								primaryColor={token('color.icon', colors.N90)}
								size="small"
							/>
						</Button>
					)}
				</Body>
				{infoBox !== undefined && (
					<InfoBox>
						{}
						{/* @ts-expect-error - TS2769 - No overload matches this call. */}
						<EmojiObjectsIcon primaryColor={token('color.icon.discovery', colors.P400)} />
						<InfoBoxContent>{infoBox}</InfoBoxContent>
					</InfoBox>
				)}
				{hasFooter && (
					<Footer>
						{steps && (
							<Position>
								{steps.current}/{steps.count}
							</Position>
						)}
						{buttons.secondary && (
							<PurpleButton appearance="subtle-link" {...omit(buttons.secondary, 'text')}>
								{buttons.secondary && buttons.secondary.text}
							</PurpleButton>
						)}
						{buttons.primary && (
							<PurpleButton autoFocus appearance="primary" {...omit(buttons.primary, 'text')}>
								{buttons.primary && buttons.primary.text}
							</PurpleButton>
						)}
					</Footer>
				)}
			</SpotlightContent>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Body = styled2.div({
	display: 'flex',
	alignItems: 'flex-start',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div + p': {
		marginTop: token('space.200', '16px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	p: {
		fontSize: '14px',
		lineHeight: '24px',
		'&:nth-of-type(n+2)': {
			marginTop: token('space.200', '16px'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&:has(+ *)': {
			marginBottom: token('space.200', '16px'),
		},
	},
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TextContent = styled2.div({
	paddingRight: token('space.100', '8px'),
	flex: '1 1 auto',
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const InfoBoxContent = styled2.p({
	flex: '1 1 auto',
	margin: `0 0 0 ${token('space.150', '12px')}`,
	lineHeight: '24px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SpotlightContent = styled2.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	marginTop: token('space.negative.200', '-16px'),
	marginBottom: token('space.negative.200', '-16px'),
	marginLeft: token('space.negative.250', '-20px'),
	marginRight: token('space.negative.250', '-20px'),
	padding: `${token('space.200', '16px')} ${token('space.200', '16px')}`,
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const StyledTitleWrapper = styled2.div({
	marginBottom: token('space.200', '16px'),
	lineHeight: '24px',
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Footer = styled2.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	marginTop: token('space.400', '32px'),
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Position = styled2.div({
	flex: '1 0 auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N70),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontFamily: codeFontFamily,
	fontSize: '11px',
	lineHeight: '20px',
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const InfoBox = styled2.div({
	display: 'flex',
	padding: `${token('space.200', '16px')} ${token('space.250', '20px')} ${token(
		'space.200',
		'16px',
	)} ${token('space.150', '12px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.discovery', colors.P50),
	marginTop: token('space.400', '32px'),
	borderRadius: '5px',
});
